// What the phone's Scan page says about one box (05 §5.2 Shell, Scan: "scan any box to see what it
// is, where it should go, and which actions apply now"). Pure: the screen hands in the box and, when
// it can read it, whether its shipment is paid; the words and the actions come from the one box state
// machine, so the page never offers what the server would refuse.
import { boxRefusal } from './box';
import { legalEventTypes } from './box-transitions';
import { activeHolds, isFinalStatus, type Box, type BoxEventType } from './box-types';
import type { Permission } from './permissions';

/** The box fields the lookup reads (a box document has these and more). */
export type LookupBox = Pick<
  Box,
  'status' | 'location' | 'currentBranchId' | 'holds' | 'flags' | 'partner' | 'lastEvent'
>;

export interface ScanLookupInput {
  box: LookupBox;
  /** The branch the phone works at, or null before one is chosen. */
  branchId: string | null;
  /**
   * Whether the box's shipment is paid in full: true or false when the phone could read the
   * shipment, undefined when it could not (the server then decides).
   */
  shipmentPaidInFull: boolean | undefined;
  holds: (permission: Permission) => boolean;
}

/** Something the person can do with the box now, and the tab that does it. */
export interface ScanAction {
  event: BoxEventType;
  /** Plain words: "Load it onto a trip". */
  label: string;
  /** The tab or page that does it: "Load". */
  where: string;
  /** Set when the server would refuse it now, in plain words ("not paid in full"). */
  refused?: string;
}

export interface ScanLookup {
  /** The status in plain words. */
  statusLabel: string;
  /** Where the box is: "Here at RUH", "On a trip", "With a partner". */
  where: string;
  /** True when the box is at the phone's branch. */
  here: boolean;
  /** Holds, flags and payment, worst first. */
  warnings: string[];
  /** What this person can do with it now (permission and box state); may be empty. */
  actions: ScanAction[];
}

export const SCAN_STATUS_LABELS: Readonly<Record<Box['status'], string>> = {
  at_branch: 'At a branch',
  on_trip: 'On a trip',
  in_container: 'In a container',
  exported: 'Shipped, at sea or in the air',
  at_port: 'At the port',
  with_partner: 'With a delivery partner',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  returned: 'Returned',
  cancelled: 'Cancelled',
  lost: 'Lost',
};

const HOLD_LABELS: Readonly<Record<string, string>> = {
  customs: 'customs',
  payment: 'payment',
  investigation: 'investigation',
  customer_request: 'the customer asked to hold it',
  address_issue: 'address problem',
};

const REFUSAL_WORDS: Readonly<Record<string, string>> = {
  unpaid: 'not paid in full',
  active_hold: 'on hold',
  blocking_hold: 'on hold',
  not_returning: 'it is not being returned',
  shipment_exported: 'the shipment has left the country',
  illegal_state: 'it cannot be scanned for this now',
};

/** The actions a scan page can point at, with the permission each needs (04). */
const ACTIONS: readonly {
  event: BoxEventType;
  label: string;
  where: string;
  permission: Permission;
  /** The box must be at the phone's branch (the row's `at_event_branch`). */
  atBranch: boolean;
}[] = [
  {
    event: 'loadTrip',
    label: 'Load it onto a trip',
    where: 'Load',
    permission: 'trip.load',
    atBranch: true,
  },
  {
    event: 'loadContainer',
    label: 'Load it into a container',
    where: 'Load',
    permission: 'container.load',
    atBranch: true,
  },
  {
    event: 'handoverToPartner',
    label: 'Hand it over to a partner',
    where: 'Hand over',
    permission: 'partner.handover',
    atBranch: true,
  },
  {
    event: 'receiveTrip',
    label: 'Receive it off its trip',
    where: 'Receive',
    permission: 'trip.unload',
    atBranch: false,
  },
  {
    event: 'receiveContainer',
    label: 'Receive it from its container',
    where: 'Receive',
    permission: 'trip.unload',
    atBranch: false,
  },
];

function whereText(box: LookupBox, branchId: string | null): string {
  const loc = box.location;
  switch (loc.kind) {
    case 'branch':
      return loc.id === branchId ? `Here at ${loc.id}` : `At ${loc.id}`;
    case 'trip':
      return 'On a trip between branches';
    case 'container':
      return box.status === 'at_port' ? 'In its container at the port' : 'In a container';
    case 'run':
      return 'On a driver’s run';
    case 'partner':
      return box.partner
        ? `With partner ${box.partner.partnerId} (${box.partner.partnerStatus.replace(/_/g, ' ')})`
        : 'With a delivery partner';
    case 'consignee':
      return 'With the consignee';
    case 'unknown':
      return 'Nobody knows where it is';
  }
}

/** What the Scan page shows for one box. */
export function lookupBox(input: ScanLookupInput): ScanLookup {
  const { box, branchId, shipmentPaidInFull, holds } = input;
  const here = branchId !== null && box.status === 'at_branch' && box.currentBranchId === branchId;

  const warnings: string[] = [];
  for (const h of activeHolds(box)) {
    warnings.push(`On hold: ${HOLD_LABELS[h.type] ?? h.type}${h.reason ? ` (${h.reason})` : ''}`);
  }
  if (box.flags.unaccounted) warnings.push('Not accounted for: it was missing at the last count');
  if (box.flags.damaged) warnings.push('Reported damaged');
  if (box.flags.returning) warnings.push('Being returned to the sender');
  if (box.flags.abandoned) warnings.push('Abandoned');
  if (shipmentPaidInFull === false && !isFinalStatus(box.status)) {
    warnings.push('Not paid in full: it cannot go in a container or to a partner until it is');
  }
  if (box.status === 'at_branch' && branchId !== null && !here) {
    warnings.push(`Not at your branch: it was last received at ${box.currentBranchId}`);
  }

  const legal = new Set(legalEventTypes(box.status));
  // The payment gate: known from the shipment, else assumed met (the server decides).
  const ctx = { shipmentPaidInFull: shipmentPaidInFull ?? true };
  const actions: ScanAction[] = [];
  for (const a of ACTIONS) {
    if (!legal.has(a.event) || !holds(a.permission)) continue;
    if (a.atBranch && !here) continue;
    const reason = boxRefusal(box as Box, a.event, ctx);
    actions.push({
      event: a.event,
      label: a.label,
      where: a.where,
      ...(reason ? { refused: REFUSAL_WORDS[reason] ?? reason.replace(/_/g, ' ') } : {}),
    });
  }

  return {
    statusLabel: SCAN_STATUS_LABELS[box.status],
    where: whereText(box, branchId),
    here,
    warnings,
    actions,
  };
}

/** A box event in plain words, for the list of scans. */
export const SCAN_EVENT_LABELS: Readonly<Record<BoxEventType, string>> = {
  collect: 'Collected',
  loadTrip: 'Loaded on a trip',
  unloadFromTrip: 'Taken off a trip',
  receiveTrip: 'Received off a trip',
  loadContainer: 'Loaded in a container',
  removeFromContainer: 'Taken out of a container',
  exportContainer: 'Left the country',
  containerArrived: 'Arrived at the port',
  receiveContainer: 'Received from its container',
  handoverToPartner: 'Handed to a partner',
  partnerUpdate: 'Partner update',
  returnFromPartner: 'Back from a partner',
  startRun: 'Out on a delivery run',
  deliverStop: 'Delivered',
  closeRun: 'Back from a run',
  completeReturn: 'Returned to the sender',
  cancelShipment: 'Cancelled',
  writeOff: 'Written off',
  found: 'Found',
};
